import { ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { BlackMarcoLogo } from '../../componentsv2/MarcoLogo';
import CoBrandedLogo from '../../componentsv2/CoBrandedLogo';
import Skeleton from '../../componentsv2/Skeleton';
import { usePartnerByReferralId } from '../../hooks/usePartners';
import Typography from '../../componentsv2/Typography';
import { CurrentStepIcon, InactiveStepIcon } from '../../components/Icons';
import { useAppSelector, useAppDispatch, logout } from '../../store';
import { useTranslation } from '../../hooks/i18n';
import { resetStore } from '../../utils/helpers';
import { DASHBOARD, MY_ACCOUNT } from '../../constants/auth';
import { COLORS } from '../../themev2';

interface ActivationLayoutProps {
  children: ReactNode;
  currentStep: number;
}

const SIDEBAR_BG = '#F1F6FD';

const STEP_KEYS = [
  'activation:steps.activateAccount',
  'activation:steps.companyInformation',
  'activation:steps.documents',
  'activation:steps.ownershipControl',
];

const ActivationLayout = ({ children, currentStep }: ActivationLayoutProps) => {
  const { t } = useTranslation(['activation', 'common']);
  const companyUser = useAppSelector((state) => state.companyUser.companyUser);
  // The query string is dropped on the way in from /create-password, so the
  // referral comes from the authenticated company user instead.
  const { partner, isLoadingPartner } = usePartnerByReferralId(companyUser?.referralId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const steps = STEP_KEYS.map((key, index) => ({
    number: index + 1,
    label: t(key),
  }));

  const handleLogout = () => {
    queryClient.clear();
    dispatch(logout())
      .unwrap()
      .then(() => {
        navigate('/login');
        resetStore(dispatch);
      });
    setIsUserMenuOpen(false);
  };

  if (isLoadingPartner) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.brand.white }}>
        <Box
          sx={{
            width: '240px',
            backgroundColor: SIDEBAR_BG,
            position: 'fixed',
            height: '100vh',
            padding: '24px 16px',
          }}
        >
          <Skeleton width="132px" height="23px" />
        </Box>
        <Box sx={{ marginLeft: '240px', flex: 1, padding: '48px' }}>
          <Skeleton width="100%" height="500px" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.brand.white }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: '240px',
          backgroundColor: SIDEBAR_BG,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          height: '100vh',
        }}
      >
        {/* Logo */}
        <Box sx={{ padding: '24px 16px' }} onClick={() => navigate(DASHBOARD)}>
          <CoBrandedLogo partnerLogoUrl={partner?.logoUrl} partnerName={partner?.name}>
            <BlackMarcoLogo />
          </CoBrandedLogo>
        </Box>

        {/* Step indicators */}
        <Box sx={{ flex: 1, padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '0' }}>
          {steps.map((step, index) => {
            const isActive = currentStep === step.number;
            const showLine = index < steps.length - 1;

            return (
              <Box
                key={step.number}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box
                    sx={{
                      width: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isActive ? <CurrentStepIcon width={14} height={14} /> : <InactiveStepIcon />}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: isActive ? COLORS.indigo[600] : COLORS.brand.night,
                      lineHeight: '21px',
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>

                {showLine && (
                  <Box
                    sx={{
                      marginLeft: '6px',
                      width: '2px',
                      height: '40px',
                      backgroundColor: '#B8C8DE',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* User info at bottom */}
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Avatar
              sx={{
                backgroundColor: COLORS.brand.night,
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px', color: COLORS.brand.paper }}
              >
                {companyUser?.user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
              </Typography>
            </Avatar>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '21px',
                color: COLORS.brand.night,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {companyUser?.user?.firstName} {companyUser?.user?.lastName}
            </Typography>
          </Box>
          <div ref={menuRef}>
            {isUserMenuOpen && (
              <Box
                sx={{
                  zIndex: 1000,
                  position: 'absolute',
                  bottom: '56px',
                  right: '12px',
                  width: '144px',
                  backgroundColor: COLORS.brand.paper,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <List>
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: COLORS.gray[300] },
                    }}
                    onClick={() => {
                      navigate(MY_ACCOUNT);
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '21px' }}>
                      {t('common:myAccount')}
                    </Typography>
                  </ListItem>
                  <Box sx={{ height: '1px', backgroundColor: COLORS.gray[500], margin: '0 8px' }} />
                  <ListItem
                    onClick={handleLogout}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: COLORS.gray[300] },
                    }}
                  >
                    <Typography
                      sx={{ color: '#930909', fontSize: '14px', fontWeight: 700, lineHeight: '21px' }}
                    >
                      {t('common:logout')}
                    </Typography>
                  </ListItem>
                </List>
              </Box>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsUserMenuOpen(!isUserMenuOpen);
              }}
              sx={{
                backgroundColor: isUserMenuOpen ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                borderRadius: '4px',
              }}
            >
              <MoreHoriz sx={{ color: COLORS.gray[1000] }} />
            </IconButton>
          </div>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          marginLeft: '240px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: COLORS.brand.white,
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            flex: 1,
            padding: '60px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ActivationLayout;
